<template>
  <div class="manual-shell">
    <!-- <header class="page-header">公共导航</header> -->
    <div class="app-shell" :class="appShellClasses">
      <aside class="sidebar" :class="{ 'mobile-open': mobileSidebarOpen }">
        <div class="sidebar__search">
          <label class="searchbox" for="navSearch">
            <img src="@/assets/search.svg" alt="" aria-hidden="true">
            <input
              id="navSearch"
              v-model.trim="keyword"
              type="text"
              autocomplete="off"
              placeholder="搜索文档导航"
              aria-label="搜索文档导航"
            />
            <button
              v-if="keyword"
              class="searchbox__clear"
              type="button"
              aria-label="清除搜索"
              @click.prevent="clearSearch"
            >
              <span aria-hidden="true">×</span>
            </button>
          </label>
        </div>

        <nav class="sidebar__nav" aria-label="文档导航">
          <div class="nav-tree">
            <div class="nav-group">
              <div
                v-for="item in visibleMenuItems"
                :key="item.node.id"
                class="nav-item"
              >
                <button
                  class="nav-row"
                  :class="{
                    'is-active': activeArticleId === item.node.id,
                    'is-open': item.hasChildren && isNodeExpanded(item.node.id),
                    'is-child': item.depth > 0
                  }"
                  type="button"
                  :style="navRowStyle(item.depth)"
                  @click="onMenuItemClick(item)"
                >
                  <span class="nav-number">{{ item.number }}</span>
                  <span class="nav-label">{{ item.node.title }}</span>
                  <span :class="item.hasChildren ? 'nav-arrow' : 'nav-bullet'" aria-hidden="true"></span>
                </button>
              </div>
            </div>
            <p v-if="!visibleMenuItems.length" class="nav-empty">没有找到匹配项，试试更短的关键词。</p>
          </div>
        </nav>

        <button class="sidebar__collapse" type="button" aria-label="收起侧边栏" @click="toggleSidebarCollapse">
          <span></span>
          <span></span>
          <span></span>
        </button>
      </aside>

      <div class="content-shell">
        <header class="hero">
          <div class="hero__inner">
            <button class="menu-trigger" type="button" aria-label="打开导航" @click="toggleMenuTrigger">
              <span></span>
              <span></span>
              <span></span>
            </button>

            <div class="hero__breadcrumb">{{ breadcrumbText }}</div>
            <h1 class="hero__title">{{ selectedArticle ? selectedArticle.title : '用户功能手册' }}</h1>

            <div class="hero__meta">
              <span class="hero__updated">{{ formattedUpdatedAt }}</span>
            </div>

            <nav class="mobile-toc" aria-label="移动端章节导航">
              <button
                v-for="item in navigationArticles"
                :key="`mobile-toc-${item.id}`"
                class="mobile-toc__link"
                :class="{ 'is-active': activeArticleId === item.id }"
                type="button"
                @click="selectArticle(item.id)"
              >
                {{ item.title }}
              </button>
            </nav>

            <div class="hero__progress">
              <span :style="{ width: readingProgressWidth }"></span>
            </div>
          </div>
        </header>

        <div class="page-layout">
          <main class="article">
            <div class="article-wrap" v-if="selectedArticle">
              <template v-if="articleHtmlContent">
                <div
                  ref="articleContent"
                  class="article-content"
                  v-html="articleHtmlContent"
                ></div>
              </template>
              <p v-else-if="articleHtmlLoading" class="frame-tip">
                正文加载中...
              </p>
              <p v-else class="frame-tip">
                未找到对应的 HTML 文件，请检查 `assets/html` 目录与文章标题是否一致。
              </p>
            </div>
          </main>

          <div
            v-if="showImageModal"
            class="image-modal"
            @click="closeImageModal"
          >
            <img
              :src="currentImageSrc"
              alt="预览图片"
              class="image-modal__img"
              @click.stop
            />
            <button class="image-modal__close" @click="closeImageModal">
              ×
            </button>
          </div>

          <aside class="article-sidebars">
            <section class="panel">
              <h2 class="panel__title">本文导读</h2>
              <nav v-if="articleTocItems.length" class="panel__nav" aria-label="本文目录">
                <button
                  v-for="item in articleTocItems"
                  :key="`toc-${item.id}`"
                  class="toc-link"
                  type="button"
                  @click="scrollToToc(item)"
                >
                  {{ item.title }}
                </button>
              </nav>
              <p v-else-if="tocLoading" class="panel__empty">本文目录加载中...</p>
              <p v-else class="panel__empty">暂无数据</p>
            </section>
          </aside>
        </div>
        <footer class="page-footer">
          深圳市肥迪科技有限公司 Copyright © 1993-2026 Feidee.All Rights Reserved. &nbsp;
          <a href="https://beian.miit.gov.cn/" target="_blank">粤ICP备2021010135号</a> &nbsp;版本：v5.0.0
        </footer>
      </div>
    </div>

    <div v-if="mobileSidebarOpen" class="overlay" @click="mobileSidebarOpen = false"></div>
    <IframeModal :visible="showIframeModal" :url="iframeUrl" @close="closeIframeModal" />
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import { documentApi, DocumentTreeNode } from '@/api/document'
import IframeModal from '@/components/IframeModal.vue'

interface MenuNode {
  id: string
  title: string
  level: number
  link: string
  order: number
  children: MenuNode[]
}

interface Article {
  id: string
  title: string
  link: string
  parents: string[]
  htmlPath?: string
  updatedAt?: number
  order?: number
}

interface TocItem {
  id: string
  title: string
}

interface MenuItem {
  node: MenuNode
  depth: number
  hasChildren: boolean
  number: string
}

const transformHandbookToMenuNodes = (nodes: DocumentTreeNode[], level = 0): MenuNode[] => {
  return nodes.map((node) => {
      const isDocument = node.type === 'document'
      const link = isDocument && node.htmlPath ? node.htmlPath : ''
      const children = node.children || []
      const hasChildren = children.length > 0
      
      return {
        id: node.id,
        title: node.title.replace(/\.md$/, ''),
        level,
        link,
        order: node.order,
        children: hasChildren ? transformHandbookToMenuNodes(children, level + 1) : [],
      }
    }).sort((a, b) => a.order - b.order)
}

const buildArticlesFromHandbook = (nodes: DocumentTreeNode[], parents: string[] = []): Article[] => {
  const result: Article[] = []

  nodes.forEach((node) => {
    if (node.type === 'document' && node.htmlPath) {
      result.push({
        id: node.id,
        title: node.title.replace(/\.md$/, ''),
        link: node.htmlPath,
        parents: [...parents],
        htmlPath: node.htmlPath,
        updatedAt: node.updatedAt,
        order: node.order,
      })
    }

    if (node.type === 'directory' && node.children && node.children.length > 0) {
      result.push(...buildArticlesFromHandbook(node.children, [...parents, node.title.replace(/\.md$/, '')]))
    }
  })

  return result.sort((a, b) => (a.parents.length - b.parents.length) || (a.order !== undefined && b.order !== undefined ? a.order - b.order : 0))
}

const findFirstArticle = (node?: MenuNode): MenuNode | null => {
  if (!node) {
    return null
  }
  if (node.link) {
    return node
  }
  return node.children.reduce<MenuNode | null>(
    (article, child) => article || findFirstArticle(child),
    null,
  )
}

export default Vue.extend({
  name: 'ManualCenter',
  components: {
    IframeModal,
  },
  data() {
    return {
      keyword: '',
      sidebarCollapsed: false,
      mobileSidebarOpen: false,
      isMobileView: typeof window !== 'undefined' ? window.innerWidth <= 960 : false,
      expandedMap: {} as Record<string, boolean>,
      ancestorIdMap: {} as Record<string, string[]>,
      activeArticleId: '',
      tree: [] as MenuNode[],
      searchTree: [] as MenuNode[],
      articles: [] as Article[],
      articleTocItems: [] as TocItem[],
      tocLoading: false,
      tocLoadToken: 0,
      articleHtmlContent: '',
      articleHtmlLoading: false,
      articleHtmlLoadToken: 0,
      showImageModal: false,
      currentImageSrc: '',
      showIframeModal: false,
      iframeUrl: '',
      searchLoading: false,
      frameResizeHandler: null as ((() => void) | null),
      loading: true,
    }
  },
  computed: {
    loweredKeyword(): string {
      return this.keyword.toLowerCase()
    },
    appShellClasses(): Record<string, boolean> {
      return {
        'is-sidebar-hidden': this.sidebarCollapsed && !this.isMobileView,
        'is-sidebar-open': this.mobileSidebarOpen && this.isMobileView,
      }
    },
    filteredTree(): MenuNode[] {
      if (!this.loweredKeyword) {
        return this.tree
      }
      return this.filterTree(this.tree, this.loweredKeyword)
    },
    visibleMenuItems(): MenuItem[] {
      const output: MenuItem[] = []
      this.flattenVisible(this.keyword ? this.searchTree : this.tree, 0, output)
      return output
    },
    filteredArticles(): Article[] {
      if (!this.loweredKeyword) {
        return this.articles
      }

      return this.articles.filter((article) => {
        const titleHit = article.title.toLowerCase().includes(this.loweredKeyword)
        const parentHit = article.parents.some((name) => name.toLowerCase().includes(this.loweredKeyword))
        return titleHit || parentHit
      })
    },
    navigationArticles(): Article[] {
      return this.articles
    },
    selectedArticle(): Article | null {
      if (!this.activeArticleId) {
        return this.articles[0] || null
      }

      const found = this.navigationArticles.find((article) => article.id === this.activeArticleId)
      if (found) {
        return found
      }

      return null
    },
    breadcrumbText(): string {
      if (!this.selectedArticle) {
        return '用户功能手册'
      }
      if (!this.selectedArticle.parents.length) {
        return '用户功能手册'
      }
      return this.selectedArticle.parents.join(' / ')
    },
    formattedUpdatedAt(): string {
      const updatedAt = this.selectedArticle?.updatedAt
      if (!updatedAt) {
        return '更新时间未知'
      }
      const date = new Date(updatedAt)
      if (Number.isNaN(date.getTime())) {
        return '更新时间未知'
      }
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `更新于 ${y}-${m}-${d}`
    },
    selectedIndex(): number {
      const article = this.selectedArticle
      if (!article) {
        return -1
      }
      return this.navigationArticles.findIndex((item) => item.id === article.id)
    },
    readingProgressWidth(): string {
      if (!this.navigationArticles.length || this.selectedIndex < 0) {
        return '0%'
      }
      const progress = ((this.selectedIndex + 1) / this.navigationArticles.length) * 100
      return `${progress.toFixed(2)}%`
    },
    previousArticle(): Article | null {
      if (this.selectedIndex <= 0) {
        return null
      }
      return this.navigationArticles[this.selectedIndex - 1]
    },
    nextArticle(): Article | null {
      if (this.selectedIndex < 0 || this.selectedIndex >= this.navigationArticles.length - 1) {
        return null
      }
      return this.navigationArticles[this.selectedIndex + 1]
    },
  },
  watch: {
    keyword(newKeyword) {
      this.performSearch(newKeyword)
    },
    selectedArticle: {
      immediate: true,
      handler(article: Article | null) {
        this.loadArticleToc(article)
        this.loadArticleHtml(article)
      },
    },
  },
  async created() {
    await this.loadTreeData()
    this.buildAncestorMap(this.tree)
    this.expandDefault(this.tree, 0)
    const defaultArticle = findFirstArticle(this.tree[0]?.children[0]) || this.articles[0]
    if (defaultArticle) {
      this.selectArticle(defaultArticle.id)
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    const frame = this.$refs.articleFrame as HTMLIFrameElement | undefined
    if (frame && frame.contentWindow && this.frameResizeHandler) {
      frame.contentWindow.removeEventListener('resize', this.frameResizeHandler)
    }
  },
  methods: {
    async performSearch(keyword: string) {
      if (!keyword || keyword.trim() === '') {
        this.searchTree = []
        return
      }

      this.searchLoading = true
      this.searchTree = []
      try {
        const data = await documentApi.search(keyword, 1000)
        if (data.results && data.results.handbook && data.results.handbook.length > 0) {
          this.searchTree = transformHandbookToMenuNodes(data.results.handbook || [])
          // console.log('Search results:', this.searchTree)
          this.expandDefault(this.searchTree, 0)
        }
      } catch (error) {
        console.error('Failed to search:', error)
        this.searchTree = []
      } finally {
        this.searchLoading = false
      }
    },
    async loadTreeData() {
      try {
        const data = await documentApi.getTree()
        if (data.handbook && data.handbook.length > 0) {
          this.tree = transformHandbookToMenuNodes(data.handbook)
          this.articles = buildArticlesFromHandbook(data.handbook)
        }
      } catch (error) {
        console.error('Failed to load tree data:', error)
        this.tree = []
        this.articles = []
      } finally {
        this.loading = false
      }
    },
    handleResize() {
      this.isMobileView = window.innerWidth <= 960
      if (!this.isMobileView) {
        this.mobileSidebarOpen = false
      }
    },
    filterTree(nodes: MenuNode[], keyword: string): MenuNode[] {
      return nodes.reduce((acc: MenuNode[], node) => {
        const children = this.filterTree(node.children, keyword)
        const matched = node.title.toLowerCase().includes(keyword)
        if (matched || children.length > 0) {
          acc.push({
            ...node,
            children,
          })
        }
        return acc
      }, [])
    },
    flattenVisible(nodes: MenuNode[], depth: number, output: MenuItem[], parentNumbers: number[] = []) {
      const sortedNodes = [...nodes].sort((a, b) => a.order - b.order)
      sortedNodes.forEach((node, index) => {
        const currentNumbers = [...parentNumbers, index + 1]
        const number = currentNumbers.map((num) => {
          if (depth === 0) {
            return this.numberToChinese(index)
          }
          return num.toString()
        }).join('.')
        const hasChildren = node.children.length > 0
        output.push({
          node,
          depth,
          hasChildren,
          number,
        })

        if (hasChildren && this.isNodeExpanded(node.id)) {
          this.flattenVisible(node.children, depth + 1, output, currentNumbers)
        }
      })
    },
    numberToChinese(index: number): string {
      const chineseNumbers = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十', '十一', '十二', '十三', '十四', '十五', '十六', '十七', '十八', '十九', '二十']
      if (index <= 19) {
        return chineseNumbers[index]
      }
      return index.toString()
    },
    isNodeExpanded(nodeId: string): boolean {
      return !!this.expandedMap[nodeId]
    },
    toggleNode(nodeId: string) {
      this.$set(this.expandedMap, nodeId, !this.expandedMap[nodeId])
    },
    expandDefault(nodes: MenuNode[], depth: number) {
      nodes.forEach((node) => {
        if (node.children.length > 0 && depth < 1) {
          this.$set(this.expandedMap, node.id, true)
        }
        this.expandDefault(node.children, depth + 1)
      })
    },
    buildAncestorMap(nodes: MenuNode[], stack: string[] = []) {
      nodes.forEach((node) => {
        this.$set(this.ancestorIdMap, node.id, [...stack])
        this.buildAncestorMap(node.children, [...stack, node.id])
      })
    },
    expandToNode(nodeId: string) {
      const parents = this.ancestorIdMap[nodeId] || []
      parents.forEach((parentId) => this.$set(this.expandedMap, parentId, true))
    },
    selectArticle(articleId: string) {
      if (!this.articles.find((item) => item.id === articleId)) {
        return
      }
      this.activeArticleId = articleId
      this.expandToNode(articleId)
      if (this.isMobileView) {
        this.mobileSidebarOpen = false
      }
    },
    onMenuItemClick(item: MenuItem) {
      if (item.hasChildren) {
        this.toggleNode(item.node.id)
      }
      if (item.node.link) {
        this.selectArticle(item.node.id)
      }
    },
    syncActiveArticle() {
      if (!this.filteredArticles.length) {
        this.activeArticleId = ''
        return
      }

      const inCurrentScope = this.navigationArticles.some((article) => article.id === this.activeArticleId)
      if (!inCurrentScope) {
        this.selectArticle(this.filteredArticles[0].id)
      }
    },
    toggleSidebarCollapse() {
      if (this.isMobileView) {
        this.mobileSidebarOpen = false
        return
      }
      this.sidebarCollapsed = !this.sidebarCollapsed
    },
    toggleMenuTrigger() {
      if (this.isMobileView) {
        this.mobileSidebarOpen = !this.mobileSidebarOpen
        return
      }
      this.sidebarCollapsed = !this.sidebarCollapsed
    },
    clearSearch() {
      this.keyword = ''
    },
    goPrevious() {
      if (this.previousArticle) {
        this.selectArticle(this.previousArticle.id)
      }
    },
    goNext() {
      if (this.nextArticle) {
        this.selectArticle(this.nextArticle.id)
      }
    },
    async loadArticleToc(article: Article | null) {
      const token = this.tocLoadToken + 1
      this.tocLoadToken = token
      this.articleTocItems = []

      if (!article) {
        this.tocLoading = false
        return
      }

      this.tocLoading = true

      if (token === this.tocLoadToken) {
        this.tocLoading = false
      }
    },
    async loadArticleHtml(article: Article | null) {
      const token = this.articleHtmlLoadToken + 1
      this.articleHtmlLoadToken = token
      this.articleHtmlContent = ''
      this.articleTocItems = []

      if (!article) {
        this.articleHtmlLoading = false
        return
      }

      this.articleHtmlLoading = true
      try {
        if (article.htmlPath) {
          try {
            let htmlContent = await documentApi.getHtmlContent(article.htmlPath)
            if (token === this.articleHtmlLoadToken) {
              htmlContent = htmlContent.replace(/<img([^>]*)style\s*=\s*["'][^"']*["']([^>]*)>/gi, '<img$1$2>')
              htmlContent = htmlContent.replace(/<img([^>]*)width\s*=\s*["'][^"']*["']([^>]*)>/gi, '<img$1$2>')
              htmlContent = htmlContent.replace(/<img([^>]*)height\s*=\s*["'][^"']*["']([^>]*)>/gi, '<img$1$2>')
              htmlContent = this.transformVideoPreviewLinks(htmlContent)

              // 提取h4标题并生成锚点ID
              const tocItems: TocItem[] = []
              const h4Regex = /<h4[^>]*>([\s\S]*?)<\/h4>/gi
              let match
              while ((match = h4Regex.exec(htmlContent)) !== null) {
                const title = match[1].replace(/<[^>]+>/g, '').trim()
                const id = this.generateAnchorId(title)
                tocItems.push({ id, title })
              }

              // 为HTML中的h4添加ID
              htmlContent = htmlContent.replace(/<h4([^>]*)>/gi, (match, attrs) => {
                if (attrs.includes('id=')) return match
                return match
              })

              // 再次替换h4，添加ID
              tocItems.forEach(item => {
                const escapedTitle = item.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                htmlContent = htmlContent.replace(
                  new RegExp(`<h4([^>]*)>(${escapedTitle})</h4>`, 'i'),
                  `<h4$1 id="${item.id}">$2</h4>`
                )
              })

              this.articleTocItems = tocItems
              this.articleHtmlContent = htmlContent
              this.$nextTick(() => {
                this.applyImageStyles()
              })
            }
          } catch (error) {
            console.error('Failed to load article HTML:', error)
          }
        }
      } finally {
        if (token === this.articleHtmlLoadToken) {
          this.articleHtmlLoading = false
        }
      }
    },
    transformVideoPreviewLinks(htmlContent: string): string {
      if (typeof window === 'undefined') {
        return htmlContent
      }

      const normalizeVideoUrl = (url: string): string => {
        const value = url.trim()
        if (!value) {
          return ''
        }
        if (/^[a-z][a-z\d+.-]*:/i.test(value) || value.startsWith('//') || value.startsWith('/')) {
          return value
        }
        return `https://${value}`
      }

      const parser = new DOMParser()
      const doc = parser.parseFromString(htmlContent, 'text/html')
      const links = Array.from(doc.querySelectorAll('a[href]')) as HTMLAnchorElement[]

      links.forEach((link) => {
        const href = link.getAttribute('href') || ''
        let parsedUrl: URL

        try {
          parsedUrl = new URL(href, window.location.origin)
        } catch {
          return
        }

        const pathname = parsedUrl.pathname.replace(/^\/+/, '')
        if (!pathname.startsWith('video-preview')) {
          return
        }

        const videoUrl = normalizeVideoUrl(parsedUrl.searchParams.get('url') || '')
        if (!videoUrl) {
          return
        }

        const title = parsedUrl.searchParams.get('title') || ''
        const poster = link.querySelector('img')?.getAttribute('src') || ''
        const video = doc.createElement('video')
        video.setAttribute('src', videoUrl)
        video.setAttribute('controls', 'controls')
        video.setAttribute('preload', 'metadata')
        video.setAttribute('playsinline', 'playsinline')
        video.className = 'article-video'

        if (title) {
          video.setAttribute('title', title)
          video.setAttribute('aria-label', title)
        }

        if (poster) {
          video.setAttribute('poster', poster)
        }

        link.replaceWith(video)
      })

      return doc.body.innerHTML
    },
    applyImageStyles() {
      const articleContent = this.$refs.articleContent as HTMLElement | undefined
      if (!articleContent) return
      
      const applyStyle = (img: HTMLImageElement) => {
        img.style.maxWidth = '80%'
        img.style.width = 'auto'
        img.style.height = 'auto'
        img.style.display = 'block'
        img.style.marginLeft = 'auto'
        img.style.marginRight = 'auto'
        img.style.objectFit = 'contain'
        img.style.cursor = 'pointer'
        img.setAttribute('title', img.closest('a') ? '' : '点击查看大图')
      }
      
      const handleImageClick = (e: Event) => {
        const target = e.target as HTMLImageElement
        if (target.closest('a')) return
        this.openImageModal(target.src)
      }
      
      const handleLinkClick = (e: Event) => {
        const target = e.target as HTMLAnchorElement
        const link = target.closest('a') as HTMLAnchorElement | null
        if (!link) return
        
        const href = link.getAttribute('href')
        if (!href) {
          e.preventDefault()
          return
        }

        if (href.includes('book-detail')) {
          e.preventDefault()
          this.openIframeModal(href)
          return
        }
        
        const isExternal = href.startsWith('http://') || href.startsWith('https://')
        if (isExternal) {
          link.setAttribute('target', '_blank')
          link.setAttribute('rel', 'noopener noreferrer')
        } else {
          e.preventDefault()
          window.location.href = href
        }
      }
      
      const images = articleContent.querySelectorAll('img')
      images.forEach((img) => {
        applyStyle(img as HTMLImageElement)
        img.removeEventListener('click', handleImageClick)
        img.addEventListener('click', handleImageClick)
      })
      
      const links = articleContent.querySelectorAll('a')
      links.forEach((link) => {
        link.addEventListener('click', handleLinkClick)
      })
      
      setTimeout(() => {
        const allImages = articleContent.querySelectorAll('img')
        allImages.forEach((img) => {
          applyStyle(img as HTMLImageElement)
          img.removeEventListener('click', handleImageClick)
          img.addEventListener('click', handleImageClick)
        })
        
        const allLinks = articleContent.querySelectorAll('a')
        allLinks.forEach((link) => {
          link.addEventListener('click', handleLinkClick)
        })
      }, 500)
    },
    openImageModal(src: string) {
      this.currentImageSrc = src
      this.showImageModal = true
      document.body.style.overflow = 'hidden'
    },
    closeImageModal() {
      this.showImageModal = false
      this.currentImageSrc = ''
      document.body.style.overflow = ''
    },
    openIframeModal(url: string) {
      this.iframeUrl = url
      this.showIframeModal = true
      document.body.style.overflow = 'hidden'
    },
    closeIframeModal() {
      this.showIframeModal = false
      this.iframeUrl = ''
      document.body.style.overflow = ''
    },

    scrollToToc(item: TocItem) {
      const articleContent = this.$refs.articleContent as HTMLElement | undefined
      if (!articleContent) {
        return
      }

      const normalize = (value: string): string => value.trim().toLowerCase()
      const escapeForAttrSelector = (value: string): string => value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
      const anchorId = String(item.id || '').trim()
      const anchorTitle = String(item.title || '').trim()
      const escapedId = anchorId
        ? escapeForAttrSelector(anchorId)
        : ''

      const candidates: Array<Element | null> = [
        anchorId ? articleContent.querySelector(`#${anchorId}`) : null,
        anchorId ? articleContent.querySelector(`[name="${escapedId}"]`) : null,
        anchorId ? articleContent.querySelector(`[data-id="${escapedId}"]`) : null,
        anchorId ? articleContent.querySelector(`[data-anchor="${escapedId}"]`) : null,
      ]

      let target = candidates.find((node) => !!node) as HTMLElement | undefined
      if (!target && anchorTitle) {
        const allAnchors = Array.from(articleContent.querySelectorAll('[id], [name], [data-id], [data-anchor], h1, h2, h3, h4, h5, h6'))
        target = allAnchors.find((node) => normalize((node as HTMLElement).innerText || node.getAttribute('id') || node.getAttribute('name') || '') === normalize(anchorTitle)) as HTMLElement | undefined
      }

      if (!target) {
        return
      }

      try {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      } catch {
        target.scrollIntoView()
      }
    },
    navRowStyle(depth: number): Record<string, string> {
      return { paddingLeft: `${depth * 16}px` }
    },
    generateAnchorId(title: string): string {
      return title
        .toLowerCase()
        .replace(/[^\u4e00-\u9fa5a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
    },
  },
})
</script>

<style lang="less" scoped>
.manual-shell {
  --bg: #ffffff;
  --bg-soft: #f8f8f8;
  --bg-cream: linear-gradient(180deg, #fbf3ea 0%, #ffffff 78%);
  --border: #f1f1f1;
  --text-1: #222226;
  --text-2: #575757;
  --text-3: #808080;
  --text-4: #aaaaaa;
  --text-link: #155dfc;
  --accent-strong: #e58e48;
  // --accent-gradient: linear-gradient(90deg, #f1b47e 0%, #eba05e 100%);
  --accent-gradient: linear-gradient(90deg,#FEFAF6,#FEFAF6);
  --shadow-card: 0 20px 50px rgba(16, 24, 40, 0.06);
  --sidebar-width: 320px;
  --layout-max: 1088px;
  --article-width: 800px;
  --right-rail-width: 256px;
  --hero-width: calc(var(--article-width) + var(--right-rail-width) + 32px);

  min-height: 100vh;
  background: var(--bg);
  color: var(--text-1);
}

.app-shell {
  min-height: 100vh;
  display: grid;
  grid-template-columns: var(--sidebar-width) minmax(0, 1fr);
  transition: grid-template-columns 220ms ease;
}

.app-shell.is-sidebar-hidden {
  grid-template-columns: 0 minmax(0, 1fr);
}

.content-shell,
.article,
.article-sidebars {
  min-width: 0;
}

@media (min-width: 1101px) {
  .content-shell {
    height: 100vh;
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .page-layout {
    flex: 1 0 auto;
    padding-bottom: 24px;
  }

  .article {
    display: block;
  }

  .article-wrap {
    height: auto;
  }
}

.sidebar {
  position: sticky;
  top: 0;
  height: 100vh;
  padding-left: 40px;
  border-right: 1px solid var(--border);
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 30;
  transition: transform 220ms ease, opacity 220ms ease;
}

.app-shell.is-sidebar-hidden .sidebar {
  transform: translateX(-100%);
  opacity: 0;
  pointer-events: none;
}

.sidebar__search {
  padding: 12px 12px 12px 16px;
  border-bottom: 1px solid var(--border);
}

.searchbox {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 30px;
  padding: 0 9px;
  border: 1px solid var(--border);
  background: var(--bg-soft);
  border-radius: 4px;
  color: var(--text-4);
}

.searchbox img {
  width: 14px;
  height: 14px;
  flex: 0 0 auto;
}

.searchbox input {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  border: 0;
  outline: none;
  background: transparent;
  color: var(--text-2);
  font-size: 12px;
}

.searchbox__clear {
  width: 28px;
  height: 28px;
  margin-right: -8px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--text-4);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 28px;
  font-size: 18px;
  line-height: 1;
}

.searchbox__clear:hover {
  color: var(--text-2);
  background: rgba(0, 0, 0, 0.06);
}

.sidebar__nav {
  flex: 1 1 auto;
  overflow: auto;
  padding: 8px 0 72px 16px;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}

.sidebar__nav:hover {
  scrollbar-color: rgba(0, 0, 0, 0.16) transparent;
}

.sidebar__nav::-webkit-scrollbar {
  width: 6px;
}

.sidebar__nav::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar__nav::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 999px;
  min-height: 12px;
}

.sidebar__nav:hover::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.16);
}

.sidebar__nav::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.24);
}

.sidebar__collapse {
  position: absolute;
  right: 12px;
  bottom: 12px;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  color: var(--text-2);
  background: transparent;
  background-color: #fff;
}

.sidebar__collapse:hover {
  background: #faf7f3;
  border-color: var(--border);
}

.sidebar__collapse span {
  position: absolute;
  width: 10px;
  height: 1.5px;
  border-radius: 999px;
  background: currentColor;
}

.sidebar__collapse span:nth-child(1) {
  transform: translateY(-3px);
}

.sidebar__collapse span:nth-child(3) {
  transform: translateY(3px);
}

.nav-group {
  display: flex;
  flex-direction: column;
  .nav-item {
    padding: 5px 0;
  }
}

.nav-row {
  width: 100%;
  // height: 32px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding-right: 12px;
  color: var(--text-1);
  border: 0;
  text-align: left;
  background: transparent;
  transition: background-color 160ms ease, color 160ms ease;
  cursor: pointer;
}

// .nav-row:hover {
//   background: #faf7f3;
// }

.nav-row.is-active {
  background: var(--accent-gradient);
  padding: 6px;
  color: #eba05e;
}

.nav-row.is-open {
  // color: var(--text-3);
}

.nav-row.is-open.is-child {
  color: var(--text-1);
}

.nav-number {
  flex: 0 0 auto;
  font-size: 14px;
  line-height: 20px;
  // color: var(--text-3);
  margin-right: 6px;
  font-weight: 400;
}

.nav-label {
  flex: 1;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.15px;
  // white-space: nowrap;
  // overflow: hidden;
  // text-overflow: ellipsis;
}

.nav-arrow,
.nav-bullet {
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.nav-arrow::before {
  content: "";
  width: 6px;
  height: 6px;
  border-right: 1.6px solid currentColor;
  border-bottom: 1.6px solid currentColor;
  transform: rotate(-45deg);
  transition: transform 160ms ease;
}

.nav-row.is-open .nav-arrow::before {
  transform: rotate(45deg);
}

// .nav-bullet::before {
//   content: "";
//   width: 4px;
//   height: 4px;
//   border-radius: 50%;
//   background: currentColor;
//   opacity: 0.34;
// }

.nav-empty {
  padding: 12px 16px;
  font-size: 13px;
  line-height: 20px;
  color: var(--text-3);
}

.search-results {
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border);
  margin-bottom: 8px;
}

.search-results__header {
  padding: 8px 16px;
  font-size: 12px;
  line-height: 18px;
  color: var(--text-3);
  background: var(--bg-soft);
}

.search-results__count {
  font-weight: 500;
}

.search-result-item {
  padding-left: 8px;
}

.search-result-item.is-active {
  background: var(--accent-gradient);
  padding: 6px 8px;
  color: #eba05e;
}

.nav-tree {
  // 树形导航样式容器
}

.page-header {
  height: 30px;
  background: #333;
  color: #fff;
  text-align: right;
  line-height: 30px;
  padding-right: 12px;
}

.hero {
  background: var(--bg-cream);
  border-bottom: 1px solid rgba(241, 241, 241, 0.7);
}

.hero__inner {
  width: min(var(--hero-width), calc(100vw - 64px));
  margin: 0 auto;
  min-height: 164px;
  padding: 24px 0;
  position: relative;
}

.menu-trigger {
  position: absolute;
  top: 22px;
  left: 0;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: none;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 4px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(229, 219, 209, 0.82);
  box-shadow: 0 8px 18px rgba(78, 49, 18, 0.06);
}

.app-shell.is-sidebar-hidden .menu-trigger {
  display: inline-flex;
}

@media (min-width: 961px) {
  .app-shell.is-sidebar-hidden .menu-trigger {
    position: fixed;
    left: 12px;
    top: 22px;
    z-index: 40;
  }
}

.menu-trigger span {
  width: 16px;
  height: 1.5px;
  border-radius: 999px;
  background: var(--text-1);
}

.hero__breadcrumb {
  font-size: 12px;
  line-height: 20px;
  color: var(--text-3);
  letter-spacing: -0.15px;
}

.hero__title {
  margin: 18px 0 22px;
  font-size: 36px;
  line-height: 1;
  font-weight: 700;
  letter-spacing: -0.8px;
  color: var(--text-1);
}

.hero__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.hero__updated {
  font-size: 12px;
  line-height: 20px;
  color: var(--text-3);
}

.mobile-toc {
  display: none;
}

.hero__progress {
  position: absolute;
  inset: auto 0 0;
  height: 2px;
  background: rgba(229, 221, 212, 0.42);
  overflow: hidden;
}

.hero__progress span {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #ecba8c 0%, #e58d48 100%);
  transition: width 160ms ease-out;
}

.page-layout {
  width: min(var(--layout-max), calc(100vw - 64px));
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, var(--article-width)) var(--right-rail-width);
  gap: 32px;
  padding: 32px 0 72px;
}

.article-wrap {
  position: relative;
  margin-top: 12px;
  display: block;
  overflow: visible;
  background: linear-gradient(180deg, #fcfbfa 0%, #f7f4ef 100%);
  // border: 1px solid #f3ede6;
  // border-radius: 20px;
  // padding: 18px 18px 16px;
  // box-shadow: var(--shadow-card);
}

.article-tools {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 18px;
}

.origin-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(236, 210, 185, 0.88);
  box-shadow: 0 10px 20px rgba(204, 123, 50, 0.1);
  font-size: 13px;
  color: #8b5a32;
  text-decoration: none;
}

.origin-link:hover {
  color: #fff;
  text-decoration: none;
  background: var(--accent-gradient);
  border-color: transparent;
}

.article-content {
  width: 100%;
  background: #fff;
}

.article-content /deep/ img {
  max-width: 100% !important;
  width: auto !important;
  height: auto !important;
  display: block !important;
  margin: 0 auto !important;
  object-fit: contain !important;
}

.article-content /deep/ .article-video {
  width: min(100%, 860px);
  max-height: 70vh;
  display: block;
  margin: 16px auto;
  background: #000;
  object-fit: contain;
}

.frame-tip {
  margin: 10px 0 0;
  color: var(--text-3);
  font-size: 13px;
  line-height: 20px;
}

.article-empty {
  min-height: 320px;
  display: grid;
  place-items: center;
  border: 1px dashed #e9e1d9;
  border-radius: 16px;
  color: var(--text-3);
  font-size: 14px;
  background: #fff;
}

.page-nav {
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.page-nav-item {
  min-height: 78px;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 6px;
  padding: 12px;
  text-align: left;
}

.page-nav-item:disabled {
  opacity: 0.56;
}

.page-dir {
  font-size: 12px;
  line-height: 20px;
  color: var(--text-3);
}

.page-title {
  width: 100%;
  font-size: 14px;
  line-height: 20px;
  color: var(--text-1);
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.panel {
  position: sticky;
  top: 32px;
  border-left: 1px solid var(--border);
  padding: 24px 24px 0 25px;
}

.panel__title {
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  letter-spacing: -0.15px;
}

.panel__empty {
  margin: 0;
  font-size: 13px;
  line-height: 20px;
  color: var(--text-3);
}

.panel__nav {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.toc-link {
  position: relative;
  display: block;
  padding-left: 0;
  border: 0;
  background: transparent;
  color: var(--text-2);
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.15px;
  text-align: left;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  cursor: pointer;
}

.toc-link.is-active {
  color: var(--text-1);
  font-weight: 500;
}

.toc-link.is-active::before {
  content: "";
  position: absolute;
  left: -25px;
  top: 2px;
  width: 2px;
  height: 16px;
  border-radius: 999px;
  background: var(--accent-strong);
}

.toc-link:hover {
  color: var(--text-1);
}


.overlay {
  position: fixed;
  inset: 0;
  background: rgba(18, 18, 20, 0.38);
  backdrop-filter: blur(3px);
  z-index: 25;
}

@media (max-width: 1280px) {
  .page-layout,
  .hero__inner {
    width: calc(100vw - 48px);
  }

  .page-layout {
    grid-template-columns: minmax(0, 1fr) 240px;
  }
}

@media (max-width: 1100px) {
  .page-layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .article-sidebars {
    display: grid;
    grid-template-columns: repeat(1, minmax(0, 1fr));
    gap: 24px;
    margin-bottom: 10px;
  }

  .panel {
    position: static;
    min-height: 100%;
    border: 1px solid var(--border);
    border-radius: 18px;
    padding: 20px;
    background: linear-gradient(180deg, #fffdfb 0%, #ffffff 100%);
  }

  .toc-link.is-active::before {
    display: none;
  }
}

@media (max-width: 960px) {
  .app-shell {
    grid-template-columns: minmax(0, 1fr);
  }

  .sidebar {
    position: fixed;
    left: 0;
    top: 0;
    width: min(320px, calc(100vw - 40px));
    transform: translateX(-100%);
  }

  .app-shell.is-sidebar-open .sidebar {
    transform: translateX(0);
  }

  .app-shell.is-sidebar-hidden .sidebar {
    transform: translateX(-100%);
  }

  .menu-trigger {
    display: inline-flex;
    position: fixed;
    top: 12px;
    left: 12px;
    z-index: 15;
  }

  .hero__inner {
    width: calc(100vw - 32px);
    min-height: 148px;
  }

  .hero__breadcrumb,
  .hero__title,
  .hero__meta,
  .mobile-toc {
    padding-left: 56px;
  }

  .mobile-toc {
    display: flex;
    gap: 8px;
    margin-top: 18px;
    padding-bottom: 6px;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .mobile-toc::-webkit-scrollbar {
    display: none;
  }

  .mobile-toc__link {
    flex: 0 0 auto;
    min-height: 36px;
    padding: 8px 14px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(233, 221, 212, 0.9);
    color: var(--text-2);
    font-size: 13px;
    line-height: 18px;
    box-shadow: 0 8px 18px rgba(88, 62, 33, 0.05);
    white-space: nowrap;
  }

  .mobile-toc__link.is-active {
    background: linear-gradient(90deg, rgba(241, 180, 126, 0.22), rgba(235, 160, 94, 0.18));
    border-color: rgba(232, 157, 92, 0.6);
    color: #9b5a20;
  }

  .page-layout {
    width: calc(100vw - 32px);
    padding-top: 24px;
  }

  .article-sidebars {
    order: -1;
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
    margin-bottom: 8px;
  }

  .panel {
    padding: 18px;
  }

  .panel__nav {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 10px;
  }

  .toc-link {
    padding: 8px 12px;
    border-radius: 999px;
    background: #faf8f4;
    border: 1px solid rgba(239, 232, 225, 0.9);
    font-size: 13px;
    line-height: 18px;
  }

  .toc-link.is-active::before {
    display: none;
  }
}

@media (max-width: 680px) {
  .hero__title {
    font-size: 28px;
  }

  .article-wrap {
    min-height: 58vh;
    min-height: 58dvh;
  }

  .page-nav {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 560px) {
  .hero__inner,
  .page-layout {
    width: calc(100vw - 20px);
  }
}

.page-footer {
  width: 100%;
  padding: 20px 12px;
  background: #f0f0f0;
  text-align: center;
  line-height: 20px;
  font-size: 12px;
  > a {
    color: var(--text-1);
  }
}

.image-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  cursor: pointer;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.image-modal__img {
  max-width: 90vw;
  max-height: 90vh;
  width: auto;
  height: auto;
  object-fit: contain;
  cursor: default;
  animation: zoomIn 0.2s ease-out;
}

@keyframes zoomIn {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.image-modal__close {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.image-modal__close:hover {
  background: rgba(255, 255, 255, 0.3);
}

@media (max-width: 768px) {
  .image-modal__img {
    max-width: 95vw;
    max-height: 95vh;
  }
  
  .image-modal__close {
    width: 36px;
    height: 36px;
    font-size: 24px;
    top: 15px;
    right: 15px;
  }
}
</style>
